import type { components } from "./schema"

export type User = components["schemas"]["UserRead"] & {
  network_slugs?: string[]
}

/**
 * Base URL for API requests. In dev this stays empty so Vite's proxy
 * forwards matching paths (`/api`, `/auth`, `/tracking`) to
 * http://localhost:8000. In prod we serve the frontend and backend from
 * the same origin (Cloudflare → DO App Platform static site + service) so
 * it also stays empty. Set `VITE_API_BASE_URL=https://…` only when
 * pointing the frontend at a backend on a different origin.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ""

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: unknown,
    public bodySnippet?: string,
    public requestId?: string,
  ) {
    super(message)
    this.name = "ApiError"
  }
}

/**
 * 12-char hex id sent as `X-Request-Id` on every request. The backend
 * echoes it in responses and logs it on every log line it emits for the
 * request, so customers without DevTools can read the id from the error
 * toast and support can grep logs by it directly.
 */
function mintRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 12)
  }
  return Math.random().toString(16).slice(2, 14).padEnd(12, "0")
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT"
  body?: unknown
  headers?: HeadersInit
  /** Override `credentials: "include"` (default sends the auth cookie). */
  credentials?: RequestCredentials
  signal?: AbortSignal
}

/**
 * Thin fetch wrapper used by every TanStack Query hook. Centralizes:
 *   - `credentials: "include"` so the httpOnly auth cookie (T1.3) rides
 *     along on every request without caller boilerplate.
 *   - JSON body serialization + content-type header.
 *   - FormData passthrough for multipart endpoints (T3.4): when `body` is
 *     a `FormData` instance we skip JSON serialization AND skip the
 *     `Content-Type` header so the browser can set the `multipart/form-data;
 *     boundary=…` header itself. The credentials + ApiError-throwing
 *     behaviour still applies, so callers get the same consistent surface.
 *   - Error normalization: non-2xx → throw `ApiError` carrying the status
 *     and the parsed `detail` so form code can branch on 401/422.
 */
export async function apiFetch<T>(
  path: string,
  { method = "GET", body, headers, credentials, signal }: RequestOptions = {},
): Promise<T> {
  const isMultipart = typeof FormData !== "undefined" && body instanceof FormData
  const url = `${BASE_URL}${path}`
  const clientRequestId = mintRequestId()
  let response: Response
  try {
    response = await fetch(url, {
      method,
      credentials: credentials ?? "include",
      headers: {
        "X-Request-Id": clientRequestId,
        // When the body is FormData we intentionally omit Content-Type so
        // fetch generates the correct multipart boundary. When there is no
        // body we also skip the header (GET/DELETE). Only JSON bodies get
        // the explicit application/json content type.
        ...(body !== undefined && !isMultipart
          ? { "Content-Type": "application/json" }
          : {}),
        ...headers,
      },
      body: isMultipart
        ? (body as FormData)
        : body !== undefined
          ? JSON.stringify(body)
          : undefined,
      signal,
    })
  } catch (err) {
    // Network-level failure (connection reset, DNS, offline, CORS). Rethrow
    // as ApiError so callers have a consistent error type and the status 0
    // is a legible marker in logs/UI. The client-minted id is still useful
    // here even though the server never saw it — support can correlate with
    // client-side telemetry once we add any.
    const reason = err instanceof Error ? err.message : String(err)
    const apiErr = new ApiError(
      0,
      `[${method} ${path}] network error (req=${clientRequestId}): ${reason}`,
      undefined,
      undefined,
      clientRequestId,
    )
    console.error("[apiFetch] network failure", {
      method, url, reason, requestId: clientRequestId,
    })
    throw apiErr
  }

  // Prefer the server's echoed request id (which is what landed in server
  // logs) over our client-minted one; they match in the happy path because
  // the middleware respects a well-formed inbound X-Request-Id.
  const serverRequestId = response.headers.get("x-request-id") ?? undefined
  const effectiveRequestId = serverRequestId ?? clientRequestId

  // 204 No Content → return undefined cast; every caller opts into this via
  // the generic parameter.
  if (response.status === 204) return undefined as T

  // Always buffer as text once so we can both parse JSON and surface the raw
  // body when parsing fails. Bodies are small for JSON APIs and we already
  // pay the same cost inside response.json().
  const rawText = await response.text()
  let parsed: unknown
  let parseOk = true
  if (rawText.length > 0) {
    try {
      parsed = JSON.parse(rawText)
    } catch {
      parseOk = false
    }
  } else {
    parsed = undefined
  }

  if (!response.ok) {
    const detailMsg = (() => {
      if (!parsed || typeof parsed !== "object" || !("detail" in parsed)) {
        return undefined
      }
      const raw = (parsed as { detail: unknown }).detail
      if (typeof raw === "string") return raw
      // FastAPI 422s arrive as `detail: [{ type, loc, msg, input }, ...]`.
      // Flatten the validation errors into a readable "loc: msg" list so the
      // UI toast and the Copy-details button don't surface "[object Object]".
      if (Array.isArray(raw)) {
        const lines = raw.map((item) => {
          if (item && typeof item === "object") {
            const it = item as { loc?: unknown; msg?: unknown }
            const loc = Array.isArray(it.loc) ? it.loc.join(".") : undefined
            const msg = typeof it.msg === "string" ? it.msg : undefined
            if (loc && msg) return `${loc}: ${msg}`
            if (msg) return msg
          }
          try {
            return JSON.stringify(item)
          } catch {
            return String(item)
          }
        })
        return lines.join("; ")
      }
      try {
        return JSON.stringify(raw)
      } catch {
        return String(raw)
      }
    })()
    const snippet = rawText.slice(0, 500)
    const ridTag = `req=${effectiveRequestId}`
    const message = detailMsg
      ? `[${method} ${path}] ${response.status} ${response.statusText} (${ridTag}): ${detailMsg}`
      : `[${method} ${path}] ${response.status} ${response.statusText} (${ridTag})${
          snippet ? ` — body: ${snippet}` : ""
        }`
    console.error("[apiFetch] HTTP error", {
      method,
      url,
      status: response.status,
      statusText: response.statusText,
      contentType: response.headers.get("content-type"),
      requestId: effectiveRequestId,
      bodySnippet: snippet,
    })
    throw new ApiError(
      response.status,
      message,
      parsed,
      snippet,
      effectiveRequestId,
    )
  }

  if (!parseOk) {
    // 2xx but body is not JSON — classic "proxy returned an HTML interstitial"
    // symptom (DO App Platform timeout page, Cloudflare challenge, Vite proxy
    // error page). Surface the first chunk of the body so production support
    // can tell WHICH proxy is responding.
    const snippet = rawText.slice(0, 500)
    const contentType = response.headers.get("content-type") ?? "unknown"
    console.error("[apiFetch] non-JSON 2xx response", {
      method,
      url,
      status: response.status,
      contentType,
      requestId: effectiveRequestId,
      bodySnippet: snippet,
    })
    throw new ApiError(
      response.status,
      `[${method} ${path}] ${response.status} returned non-JSON (${contentType}) (req=${effectiveRequestId}): ${snippet}`,
      undefined,
      snippet,
      effectiveRequestId,
    )
  }

  return parsed as T
}
